'use client';

import { useState, useRef } from 'react';
import {
	BusinessCardExperienceContentSchema,
	type TBusinessCardExperienceContent,
	type TOpeningHourItem,
} from '@shared/schemas';
import { SocialLinksManager } from './SocialLinksManager';
import { ALL_FACILITIES } from './facilities';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ThemeSelector } from './ThemeSelector';
import {
	Building2,
	User,
	Clock,
	MapPin,
	Share2,
	Trash2,
	Sparkles,
	ChevronDown,
	Upload,
	Bell,
	Mail,
} from 'lucide-react';

type FormValue = Partial<TBusinessCardExperienceContent>;

interface Props {
	value: FormValue;
	onChange: (value: FormValue) => void;
	onValidityChange: (valid: boolean) => void;
}

const DEFAULT_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function BusinessCardForm({ value, onChange, onValidityChange }: Props) {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [locationOpen, setLocationOpen] = useState(true);
	const [streetNumberFirst, setStreetNumberFirst] = useState(true);
	// Initialize structured address from existing value.address if present
	const [street, setStreet] = useState(() => {
		if (value.address) {
			const parts = value.address.split(',').map((s) => s.trim());
			return parts[0] || '';
		}
		return '';
	});
	const [number, setNumber] = useState('');
	const [postalCode, setPostalCode] = useState('');
	const [city, setCity] = useState(() => {
		if (value.address) {
			const parts = value.address.split(',').map((s) => s.trim());
			return parts.length >= 2 ? parts[1] || '' : '';
		}
		return '';
	});
	const [stateProvince, setStateProvince] = useState(() => {
		if (value.address) {
			const parts = value.address.split(',').map((s) => s.trim());
			return parts.length >= 3 ? parts[2] || '' : '';
		}
		return '';
	});
	const [country, setCountry] = useState(() => {
		if (value.address) {
			const parts = value.address.split(',').map((s) => s.trim());
			return parts.length >= 4 ? parts[parts.length - 1] || '' : '';
		}
		return '';
	});

	const updateFields = (patch: Partial<TBusinessCardExperienceContent>) => {
		const updated = { ...value, type: 'business-card' as const, ...patch };
		onChange(updated);
		onValidityChange(BusinessCardExperienceContentSchema.safeParse(updated).success);
	};

	const set = (key: keyof TBusinessCardExperienceContent, next: unknown) => {
		updateFields({ [key]: next } as Partial<TBusinessCardExperienceContent>);
	};

	const updateAddress = (patch: {
		street?: string;
		number?: string;
		postalCode?: string;
		city?: string;
		stateProvince?: string;
		country?: string;
		streetNumberFirst?: boolean;
	}) => {
		const nextStreet = patch.street !== undefined ? patch.street : street;
		const nextNumber = patch.number !== undefined ? patch.number : number;
		const nextPostal = patch.postalCode !== undefined ? patch.postalCode : postalCode;
		const nextCity = patch.city !== undefined ? patch.city : city;
		const nextState = patch.stateProvince !== undefined ? patch.stateProvince : stateProvince;
		const nextCountry = patch.country !== undefined ? patch.country : country;
		const nextStreetFirst =
			patch.streetNumberFirst !== undefined ? patch.streetNumberFirst : streetNumberFirst;

		if (patch.street !== undefined) setStreet(nextStreet);
		if (patch.number !== undefined) setNumber(nextNumber);
		if (patch.postalCode !== undefined) setPostalCode(nextPostal);
		if (patch.city !== undefined) setCity(nextCity);
		if (patch.stateProvince !== undefined) setStateProvince(nextState);
		if (patch.country !== undefined) setCountry(nextCountry);
		if (patch.streetNumberFirst !== undefined) setStreetNumberFirst(nextStreetFirst);

		const streetLine = nextStreetFirst
			? [nextNumber, nextStreet].filter(Boolean).join(' ')
			: [nextStreet, nextNumber].filter(Boolean).join(' ');
		const cityLine = [streetLine, nextCity].filter(Boolean).join(', ');
		const fullAddr = [cityLine, nextState, nextPostal, nextCountry]
			.map((s) => s.trim())
			.filter(Boolean)
			.join(', ');

		updateFields({
			address: fullAddr.trim() || undefined,
			mapUrl: fullAddr.trim()
				? `https://maps.google.com/?q=${encodeURIComponent(fullAddr.trim())}`
				: undefined,
		});
	};

	const clearAddress = () => {
		setStreet('');
		setNumber('');
		setPostalCode('');
		setCity('');
		setStateProvince('');
		setCountry('');
		updateFields({
			address: undefined,
			mapUrl: undefined,
		});
	};

	const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		if (file.size > 1024 * 1024) {
			alert('Image file size must be 1 MB or smaller');
			return;
		}
		const reader = new FileReader();
		reader.onload = (event) => {
			const dataUrl = event.target?.result as string;
			if (dataUrl) {
				set('logoUrl', dataUrl);
			}
		};
		reader.readAsDataURL(file);
	};

	// Opening hours helper
	const hours: TOpeningHourItem[] =
		value.openingHours && value.openingHours.length > 0
			? value.openingHours
			: DEFAULT_DAYS.map((day) => ({
					day,
					open: '08:00 am',
					close: '07:00 pm',
					isClosed: false,
				}));

	const updateHourDay = (dayIndex: number, partial: Partial<TOpeningHourItem>) => {
		const updatedHours = hours.map((item: TOpeningHourItem, idx: number) =>
			idx === dayIndex ? { ...item, ...partial } : item,
		);
		set('openingHours', updatedHours);
	};

	// Facilities helper (unselected by default, user selects what they have)
	const currentFacilities: string[] = value.facilities ?? [];
	const toggleFacility = (facilityId: string) => {
		const exists = currentFacilities.includes(facilityId);
		const next = exists
			? currentFacilities.filter((f: string) => f !== facilityId)
			: [...currentFacilities, facilityId];
		set('facilities', next);
	};

	return (
		<div className="space-y-6" aria-labelledby="business-card-title">
			<div>
				<h1 id="business-card-title" className="text-2xl font-bold tracking-tight text-slate-950">
					Create your business page
				</h1>
				<p className="mt-1.5 text-sm text-slate-600">
					Configure your digital business card. The mobile preview on the right updates in real
					time.
				</p>
			</div>

			{/* 1. BUSINESS INFORMATION (Matching Owner Settings Reference) */}
			<section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs transition hover:border-slate-300">
				<div className="flex items-center justify-between pb-4 border-b border-slate-100">
					<div className="flex items-center gap-2.5">
						<div className="p-2 rounded-xl bg-blue-50 text-blue-600">
							<Building2 className="size-5" />
						</div>
						<div>
							<h2 className="text-base font-bold text-slate-900">
								Business Information <span className="text-rose-500">*</span>
							</h2>
							<p className="text-xs text-slate-500">Introduce your business or organization.</p>
						</div>
					</div>
				</div>

				<div className="mt-4 space-y-4">
					{/* Logo Image Upload / URL with visual preview & Delete button */}
					<div>
						<Label
							htmlFor="card-logo"
							className="text-xs font-semibold text-slate-700 block mb-1.5"
						>
							Image / Logo
						</Label>
						<div className="flex flex-wrap items-center gap-4">
							{value.logoUrl ? (
								<div className="relative size-18 rounded-xl border border-blue-200 bg-white p-1.5 shadow-sm flex items-center justify-center shrink-0">
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src={value.logoUrl}
										alt="Business Logo"
										className="max-h-full max-w-full object-contain rounded-lg"
									/>
								</div>
							) : (
								<div className="size-18 rounded-xl border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-slate-400 text-xs shrink-0 font-medium text-center p-2">
									No logo
								</div>
							)}

							<div className="flex-1 min-w-[220px] space-y-2">
								<input
									type="file"
									ref={fileInputRef}
									accept="image/png,image/jpeg,image/webp,image/svg+xml"
									onChange={handleLogoUpload}
									className="hidden"
								/>
								<div className="flex items-center gap-2">
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => fileInputRef.current?.click()}
										className="h-9 text-xs px-3 flex items-center gap-1.5 cursor-pointer border-slate-300 hover:bg-slate-50"
									>
										<Upload className="size-3.5 text-slate-600" />
										Upload Image
									</Button>
									{value.logoUrl && (
										<Button
											type="button"
											variant="destructive"
											size="sm"
											onClick={() => set('logoUrl', '')}
											className="h-9 text-xs px-3 flex items-center gap-1.5 cursor-pointer"
										>
											<Trash2 className="size-3.5" />
											Delete
										</Button>
									)}
								</div>
								<Input
									id="card-logo"
									type="url"
									value={value.logoUrl ?? ''}
									onChange={(e) => set('logoUrl', e.target.value)}
									placeholder="or paste image URL: https://example.com/logo.png"
									className="text-xs h-9 bg-slate-50/50"
								/>
							</div>
						</div>
					</div>

					<div className="grid gap-4 sm:grid-cols-2">
						<Field
							label="Company Name *"
							id="card-company"
							value={value.company ?? ''}
							onChange={(next) => set('company', next)}
							placeholder="e.g. Acme Corporation"
							required
						/>
						<Field
							label="Title / Category"
							id="card-tagline"
							value={value.tagline ?? ''}
							onChange={(next) => set('tagline', next)}
							placeholder="e.g. Design & Technology Studio"
						/>
					</div>
				</div>
			</section>

			{/* 2. HEADER AURORA THEME (DYNAMIC GRADIENT) */}
			<section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs transition hover:border-slate-300">
				<div className="flex items-center justify-between pb-3 border-b border-slate-100">
					<div className="flex items-center gap-2.5">
						<div className="p-2 rounded-xl bg-purple-50 text-purple-600">
							<Sparkles className="size-5" />
						</div>
						<div>
							<h2 className="text-base font-bold text-slate-900">
								Aesthetic Theme &amp; Brand Colors
							</h2>
							<p className="text-xs text-slate-500">
								Choose a modern theme style and palette. Elements dynamically adapt in the live
								preview.
							</p>
						</div>
					</div>
					<div
						className="h-8 w-20 shrink-0 rounded-xl shadow-inner border border-white/40"
						style={{
							backgroundColor: '#090d16',
							backgroundImage: `radial-gradient(at 20% 20%, ${value.headerColor || value.accentColor || '#1d4ed8'}ee 0px, transparent 70%), radial-gradient(at 80% 80%, ${value.headerColor || value.accentColor || '#1d4ed8'}88 0px, transparent 60%)`,
						}}
						title="Live gradient preview"
					/>
				</div>

				<div className="mt-5">
					<ThemeSelector
						selectedTheme={value.theme || 'glass'}
						onThemeChange={(theme) => set('theme', theme)}
						accentColor={value.accentColor || '#059669'}
						onAccentColorChange={(color) => {
							set('accentColor', color);
							set('headerColor', color);
						}}
						headerColor={value.headerColor || value.accentColor || '#059669'}
						onHeaderColorChange={(color) => set('headerColor', color)}
					/>
				</div>
			</section>

			{/* 3. CONTACT PERSON & DIRECT COMMUNICATION */}
			<section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs transition hover:border-slate-300">
				<div className="flex items-center gap-2.5 pb-4 border-b border-slate-100">
					<div className="p-2 rounded-xl bg-emerald-50 text-emerald-600">
						<User className="size-5" />
					</div>
					<div>
						<h2 className="text-base font-bold text-slate-900">Contact Person & Communication</h2>
						<p className="text-xs text-slate-500">
							Details saved to phones via 1-tap vCard and clickable call/email/WhatsApp.
						</p>
					</div>
				</div>

				<div className="mt-4 grid gap-4 sm:grid-cols-2">
					<Field
						label="Full Name (Contact Person) *"
						id="card-name"
						value={value.fullName ?? ''}
						onChange={(next) => set('fullName', next)}
						placeholder="e.g. Jane Doe"
						required
					/>
					<Field
						label="Role / Designation"
						id="card-role"
						value={value.role ?? ''}
						onChange={(next) => set('role', next)}
						placeholder="e.g. Managing Director"
					/>
					<Field
						label="Phone Number (Click to Call)"
						id="card-phone"
						type="tel"
						value={value.phone ?? ''}
						onChange={(next) => set('phone', next)}
						placeholder="e.g. +1 (555) 234-5678"
					/>
					<Field
						label="Email Address (Click to Mail)"
						id="card-email"
						type="email"
						value={value.email ?? ''}
						onChange={(next) => set('email', next)}
						placeholder="e.g. hello@example.com"
					/>
					<Field
						label="WhatsApp Chat Number"
						id="card-whatsapp"
						type="tel"
						value={value.whatsApp ?? ''}
						onChange={(next) => set('whatsApp', next)}
						placeholder="e.g. +1 (555) 234-5678"
						className="sm:col-span-2"
					/>
				</div>
			</section>

			{/* 4. RETRACTABLE OPEN HOURS SCHEDULE */}
			<section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs transition hover:border-slate-300">
				<div className="flex items-center justify-between pb-3 border-b border-slate-100">
					<div className="flex items-center gap-2.5">
						<div className="p-2 rounded-xl bg-amber-50 text-amber-600">
							<Clock className="size-5" />
						</div>
						<div>
							<h2 className="text-base font-bold text-slate-900">Opening Hours Schedule</h2>
							<p className="text-xs text-slate-500">
								Displays a retractable dropdown with live open/closed badges.
							</p>
						</div>
					</div>
				</div>

				<div className="mt-4 space-y-2.5">
					{hours.map((item, idx) => (
						<div
							key={item.day}
							className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 text-xs"
						>
							<span className="font-semibold text-slate-800 w-24">{item.day}</span>
							<div className="flex items-center gap-2">
								<input
									type="text"
									aria-label={`${item.day} opening time`}
									value={item.open}
									disabled={item.isClosed}
									onChange={(e) => updateHourDay(idx, { open: e.target.value })}
									placeholder="08:00 am"
									className="w-22 h-8 px-2 rounded-md border border-slate-200 bg-white text-xs disabled:opacity-50"
								/>
								<span className="text-slate-400">-</span>
								<input
									type="text"
									aria-label={`${item.day} closing time`}
									value={item.close}
									disabled={item.isClosed}
									onChange={(e) => updateHourDay(idx, { close: e.target.value })}
									placeholder="07:00 pm"
									className="w-22 h-8 px-2 rounded-md border border-slate-200 bg-white text-xs disabled:opacity-50"
								/>
							</div>
							<label className="flex items-center gap-1.5 text-slate-600 cursor-pointer">
								<input
									type="checkbox"
									checked={item.isClosed}
									onChange={(e) => updateHourDay(idx, { isClosed: e.target.checked })}
									className="rounded border-slate-300 text-blue-600"
								/>
								<span>Closed</span>
							</label>
						</div>
					))}
				</div>
			</section>

			{/* 5. LOCATION & ADDRESS (Matching qr-code.io) */}
			<section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs transition hover:border-slate-300">
				<div className="flex items-center justify-between pb-4 border-b border-slate-100">
					<div className="flex items-center gap-2.5">
						<div className="p-2 rounded-xl bg-rose-50 text-rose-600">
							<MapPin className="size-5" />
						</div>
						<div>
							<h2 className="text-base font-bold text-slate-900">Location</h2>
							<p className="text-xs text-slate-500">
								Provide your address and location information.
							</p>
						</div>
					</div>
					<button
						type="button"
						onClick={() => setLocationOpen((prev) => !prev)}
						className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition cursor-pointer"
						aria-label="Toggle location section"
					>
						<ChevronDown
							className={`size-5 transform transition-transform duration-200 ${
								locationOpen ? 'rotate-180' : ''
							}`}
						/>
					</button>
				</div>

				{locationOpen && (
					<div className="mt-4 space-y-4">
						{/* Street Number First Toggle & Clear Button */}
						<div className="flex items-center justify-between pt-1">
							<div className="flex items-center gap-2.5">
								<Switch
									id="street-number-first"
									checked={streetNumberFirst}
									onCheckedChange={(checked) => {
										updateAddress({ streetNumberFirst: checked });
									}}
								/>
								<Label
									htmlFor="street-number-first"
									className="text-xs font-medium text-slate-700 cursor-pointer select-none"
								>
									Street number first
								</Label>
							</div>

							{(street || number || postalCode || city || stateProvince || country) && (
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={clearAddress}
									className="h-7 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700 font-medium cursor-pointer"
								>
									Clear address
								</Button>
							)}
						</div>

						{/* Structured Address Grid */}
						<div className="grid gap-3.5 sm:grid-cols-3">
							<Field
								label="Street / Estate"
								id="addr-street"
								value={street}
								onChange={(val) => updateAddress({ street: val })}
								placeholder="e.g. Royal Pine Estate or 100 Innovation Way"
								className="sm:col-span-1"
							/>
							<Field
								label="Number / Unit"
								id="addr-number"
								value={number}
								onChange={(val) => updateAddress({ number: val })}
								placeholder="e.g. Plot 4 or Suite 400"
								className="sm:col-span-1"
							/>
							<Field
								label="Postal Code"
								id="addr-postal"
								value={postalCode}
								onChange={(val) => updateAddress({ postalCode: val })}
								placeholder="e.g. 105102 or 94105"
								className="sm:col-span-1"
							/>
						</div>

						<div className="grid gap-3.5 sm:grid-cols-2">
							<Field
								label="City / Area"
								id="addr-city"
								value={city}
								onChange={(val) => updateAddress({ city: val })}
								placeholder="e.g. Lekki, Lagos or San Francisco"
								className="sm:col-span-1"
							/>
							<Field
								label="State / Province"
								id="addr-state"
								value={stateProvince}
								onChange={(val) => updateAddress({ stateProvince: val })}
								placeholder="e.g. Lagos State or CA"
								className="sm:col-span-1"
							/>
						</div>

						<div className="grid gap-3.5">
							<Field
								label="Country"
								id="addr-country"
								value={country}
								onChange={(val) => updateAddress({ country: val })}
								placeholder="e.g. Nigeria or United States"
							/>
						</div>

						<p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200/80 rounded-lg p-2.5 flex items-center gap-1.5">
							<MapPin className="size-3.5 text-blue-600 shrink-0" />
							<span>
								When visitors tap <strong>"Show on Map"</strong>, Google Maps opens directly with
								this exact address.
							</span>
						</p>
					</div>
				)}
			</section>

			{/* 6. SOCIAL NETWORKS */}
			<section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs transition hover:border-slate-300">
				<div className="flex items-center gap-2.5 pb-4 border-b border-slate-100">
					<div className="p-2 rounded-xl bg-cyan-50 text-cyan-600">
						<Share2 className="size-5" />
					</div>
					<div>
						<h2 className="text-base font-bold text-slate-900">Social Networks</h2>
						<p className="text-xs text-slate-500">Connect visitors to your branded channels.</p>
					</div>
				</div>

				<div className="mt-4">
					<SocialLinksManager
						links={value.socialLinks ?? []}
						onChange={(nextLinks) => set('socialLinks', nextLinks)}
					/>
				</div>
			</section>

			{/* 7. ABOUT THE COMPANY */}
			<section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs transition hover:border-slate-300">
				<div className="flex items-center gap-2.5 pb-4 border-b border-slate-100">
					<div className="p-2 rounded-xl bg-indigo-50 text-indigo-600">
						<Building2 className="size-5" />
					</div>
					<div>
						<h2 className="text-base font-bold text-slate-900">About the Company</h2>
						<p className="text-xs text-slate-500">Tell your story, mission, and services.</p>
					</div>
				</div>

				<div className="mt-4 space-y-2">
					<Textarea
						id="card-about"
						value={value.about ?? value.bio ?? ''}
						maxLength={1500}
						rows={3}
						placeholder="e.g. We specialize in innovative digital solutions, crafting seamless experiences for modern businesses worldwide."
						onChange={(e) => {
							updateFields({
								about: e.target.value,
								bio: e.target.value,
							});
						}}
					/>
				</div>
			</section>

			{/* 8. FACILITIES & AMENITIES */}
			<section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs transition hover:border-slate-300">
				<div className="flex items-center gap-2.5 pb-3 border-b border-slate-100">
					<div className="p-2 rounded-xl bg-teal-50 text-teal-600">
						<Sparkles className="size-5" />
					</div>
					<div>
						<h2 className="text-base font-bold text-slate-900">Facilities & Amenities</h2>
						<p className="text-xs text-slate-500">
							Select what visitors can enjoy at your location.
						</p>
					</div>
				</div>

				<div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
					{ALL_FACILITIES.map((item) => {
						const active = currentFacilities.includes(item.id);
						const Icon = item.icon;
						return (
							<button
								key={item.id}
								type="button"
								onClick={() => toggleFacility(item.id)}
								className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-semibold transition cursor-pointer gap-1.5 ${
									active
										? 'border-blue-600 bg-blue-50/70 text-blue-700 shadow-xs'
										: 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
								}`}
							>
								<Icon className="size-5" />
								<span>{item.label}</span>
							</button>
						);
					})}
				</div>
			</section>

			{/* 9. LEAD & CONTACT NOTIFICATIONS */}
			<section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs transition hover:border-slate-300">
				<div className="flex items-center justify-between pb-3 border-b border-slate-100">
					<div className="flex items-center gap-2.5">
						<div className="p-2 rounded-xl bg-emerald-50 text-emerald-600">
							<Bell className="size-5" />
						</div>
						<div>
							<h2 className="text-base font-bold text-slate-900">Lead & Contact Alerts</h2>
							<p className="text-xs text-slate-500">
								Get instantly alerted when someone shares their contact back with you.
							</p>
						</div>
					</div>
					<div className="flex items-center gap-2">
						<span className="text-xs font-semibold text-slate-700">
							{value.leadAlertsEnabled !== false ? 'Enabled' : 'Disabled'}
						</span>
						<Switch
							checked={value.leadAlertsEnabled !== false}
							onCheckedChange={(checked) => set('leadAlertsEnabled', checked)}
						/>
					</div>
				</div>

				{value.leadAlertsEnabled !== false && (
					<div className="mt-4 space-y-4 pt-1">
						<div className="rounded-xl bg-emerald-50/60 border border-emerald-100 p-3 text-xs text-emerald-800 flex items-start gap-2">
							<Mail className="size-4 text-emerald-600 shrink-0 mt-0.5" />
							<span>
								When a visitor submits the &ldquo;Share Contact&rdquo; form, you will receive an
								instant email alert with a <strong>.vcf contact file attachment</strong> for 1-tap
								saving into iOS, Android, or Outlook.
							</span>
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<Field
								id="card-lead-email"
								label="Alert Email Address"
								type="email"
								value={value.leadNotificationEmail ?? ''}
								onChange={(val) => set('leadNotificationEmail', val)}
								placeholder={value.email || 'e.g. alerts@company.com'}
							/>

							<Field
								id="card-lead-webhook"
								label="Webhook URL (Optional)"
								type="url"
								value={value.webhookUrl ?? ''}
								onChange={(val) => set('webhookUrl', val)}
								placeholder="https://hooks.zapier.com/..."
							/>
						</div>
						<p className="text-[11px] text-slate-400">
							Leave email blank to use the card&rsquo;s primary email address (
							{value.email || 'or account email'}). Webhooks dispatch an instant JSON POST payload
							for Zapier, Make, or CRM automations.
						</p>
					</div>
				)}
			</section>
		</div>
	);
}

function Field({
	label,
	id,
	value,
	onChange,
	type = 'text',
	required = false,
	placeholder,
	className,
}: {
	label: string;
	id: string;
	value: string;
	onChange: (value: string) => void;
	type?: string;
	required?: boolean;
	placeholder?: string;
	className?: string;
}) {
	return (
		<div className={`space-y-1.5 ${className ?? ''}`}>
			<Label htmlFor={id} className="text-xs font-semibold text-slate-700">
				{label}
			</Label>
			<Input
				id={id}
				type={type}
				value={value}
				required={required}
				placeholder={placeholder}
				maxLength={type === 'email' ? 320 : 240}
				onChange={(event) => onChange(event.target.value)}
				className="h-9 text-xs"
			/>
		</div>
	);
}
